/**
 * SCRUM-1032 review, round 7 — what a grid keeps staged after a save.
 *
 * The config grids used to finish a save by replacing their staged rows with
 * the copy taken when the save started, minus what landed. Anything staged
 * while the requests were out — a quick-add chip clicked, a name retyped — was
 * not in that copy and vanished without a word. These helpers work on the
 * CURRENT staged state instead, and take out only what was sent and landed.
 */

/**
 * The pending edits left once a save has landed some of them.
 *
 * An edit is retired field by field, and only while the field still holds the
 * value that was sent: a value typed during the write is newer than the one
 * saved and stays staged for the next pass. Edits the save did not send (or
 * sent and failed) are left exactly as they are.
 *
 * Returns `current` itself when nothing changes, so a state setter can bail.
 */
export const retireSavedEdits = <E extends object>(
  current: Map<string, E>,
  landed: ReadonlyArray<readonly [string, E]>,
): Map<string, E> => {
  let next: Map<string, E> | null = null

  landed.forEach(([id, sent]) => {
    const source = next ?? current
    const staged = source.get(id) as Record<string, unknown> | undefined
    if (!staged) return

    const remaining: Record<string, unknown> = { ...staged }
    let changed = false
    Object.entries(sent as Record<string, unknown>).forEach(([field, value]) => {
      if (field in remaining && Object.is(remaining[field], value)) {
        delete remaining[field]
        changed = true
      }
    })
    if (!changed) return

    next = next ?? new Map(current)
    if (Object.keys(remaining).length === 0) next.delete(id)
    else next.set(id, remaining as E)
  })

  return next ?? current
}

/**
 * `retireSavedEdits` for a grid whose update REPLACES the row, so each staged
 * edit is the whole record (Soil, Emitter).
 *
 * Retiring field by field there leaves a fragment: rename, then type another
 * field while the PUT is out, and what stays staged is `{ efficiency: 70 }`
 * with no name — shown as a blank name, and sent next pass as a record with
 * its name missing. Here a record is retired only when every field still holds
 * what was sent; otherwise the whole, newer record stays staged as it is.
 */
export const retireSavedRecords = <E extends object>(
  current: Map<string, E>,
  landed: ReadonlyArray<readonly [string, E]>,
): Map<string, E> => {
  let next: Map<string, E> | null = null

  landed.forEach(([id, sent]) => {
    const source = next ?? current
    const staged = source.get(id) as Record<string, unknown> | undefined
    if (!staged) return
    const sentFields = sent as Record<string, unknown>
    const fields = new Set([...Object.keys(staged), ...Object.keys(sentFields)])
    const unchanged = Array.from(fields).every((field) =>
      Object.is(staged[field], sentFields[field]),
    )
    if (!unchanged) return

    next = next ?? new Map(current)
    next.delete(id)
  })

  return next ?? current
}

/**
 * The staged new rows left once a save has created some of them: the current
 * list minus the ones that landed, by id. Rows added while the save was
 * running are kept.
 */
export const retireCreatedDrafts = <D extends { id: unknown }>(
  current: ReadonlyArray<D>,
  landedIds: ReadonlyArray<unknown>,
): D[] => {
  if (landedIds.length === 0) return [...current]
  const landed = new Set(landedIds)
  return current.filter((draft) => !landed.has(draft.id))
}

/**
 * The pending edits a save may send: those whose row is in the list it
 * writes against. An edit left on a row that was deleted (or, on /config,
 * filtered out by a search) is neither sent nor checked — sending it 404s, and
 * checking it refused every later save over a cell nobody could see.
 * It stays staged, untouched.
 */
export const editsForRows = <E>(
  edits: ReadonlyMap<string, E>,
  rowIds: ReadonlySet<string>,
): Array<[string, E]> =>
  Array.from(edits.entries()).filter(([id]) => rowIds.has(id))

/**
 * Which pending edits a save sends, and which it can drop.
 *
 * `knownRowIds` are the rows the grid has fetched — kept across a search, so a
 * row a search has hidden is still known (SCRUM-1032 review: Save on /config
 * used to skip an edit on a hidden row and report success, and the value was
 * gone on reload). Every edit on a known row is sent, hidden or not; the
 * edit holds the whole of what the request needs.
 *
 * `listIsComplete` says the last fetch had no search, so a row missing from
 * it no longer exists. An edit on such a row cannot land — sending it 404s on
 * every pass and held the wizard step for good — so it is returned in `gone`
 * for the caller to drop. While a search is on, nothing is called gone.
 */
export const splitEditsForSave = <E>(
  edits: ReadonlyMap<string, E>,
  knownRowIds: ReadonlySet<string>,
  listIsComplete: boolean,
): { send: Array<[string, E]>; gone: string[] } => {
  const send: Array<[string, E]> = []
  const gone: string[] = []
  edits.forEach((edit, id) => {
    if (knownRowIds.has(id)) send.push([id, edit])
    else if (listIsComplete) gone.push(id)
  })
  return { send, gone }
}

/**
 * The rows a grid knows after a fetch. An unfiltered fetch is the whole list
 * and replaces what was known (dropping deleted rows); a searched one only
 * adds to it, so the rows the search hid stay known.
 */
export const mergeKnownRows = <R extends { id: unknown }>(
  known: ReadonlyMap<string, R>,
  fetched: ReadonlyArray<R>,
  isComplete: boolean,
): Map<string, R> => {
  const next = new Map(isComplete ? [] : known)
  fetched.forEach((row) => next.set(String(row.id), row))
  return next
}

/** `map` without `id`, or `map` itself when it has no such key. */
export const withoutKey = <V>(map: Map<string, V>, id: string): Map<string, V> => {
  if (!map.has(id)) return map
  const next = new Map(map)
  next.delete(id)
  return next
}
